import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
   Box,
   Paper,
   Typography,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   ListItemSecondaryAction,
   Avatar,
   IconButton,
   Button,
   Chip,
   Divider,
   CircularProgress,
   Tooltip,
   Badge,
} from '@mui/material';
import {
   PersonAdd as PersonAddIcon,
   Refresh as RefreshIcon,
   ArrowForward as ArrowForwardIcon,
   CheckCircle as CheckCircleIcon,
   Cancel as CancelIcon,
   HourglassEmpty,
   HourglassEmpty as PendingIcon,
   Send as SendIcon,
   Group as GroupIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { resendInvitation } from '../../services/tripParticipantService';

// Interface for the invitation object
interface Invitation {
   id: string;
   tripId: string;
   tripTitle: string;
   email: string;
   status: 'pending' | 'accepted' | 'declined';
   createdAt: Date;
   expiresAt: Date;
}

const InvitationStatus: React.FC<{
   declinedCount: number;
   acceptedCount: number;
   pendingCount: number;
}> = ({ declinedCount, acceptedCount, pendingCount }) => {
   const navigate = useNavigate();

   // Navigate to create a new trip
   const handleCreateTrip = () => {
      try {
         // First try the trips/new route
         navigate('/trips');
      } catch (err) {
         console.error('Error navigating to new trip page:', err);
         // Fallback to the trips page if /trips/new isn't available
         navigate('/trips');
      }
   };

   return (
      <>
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               mb: 2,
               p: 2,
            }}
         >
            <Typography variant='h6'>Invitation Status</Typography>
            <Button size='small' startIcon={<PersonAddIcon />} component={Link} to='/invitations'>
               Invite Travel Buddies
            </Button>
         </Box>

         {/* Stats row */}
         <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
               <Badge
                  badgeContent={pendingCount}
                  color='warning'
                  sx={{ '& .MuiBadge-badge': { top: 5, right: 5 } }}
               >
                  <Avatar sx={{ bgcolor: 'warning.light', mb: 1, mx: 'auto' }}>
                     <PendingIcon />
                  </Avatar>
               </Badge>
               <Typography variant='body2'>Pending</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
               <Badge
                  badgeContent={acceptedCount}
                  color='success'
                  sx={{ '& .MuiBadge-badge': { top: 5, right: 5 } }}
               >
                  <Avatar sx={{ bgcolor: 'success.light', mb: 1, mx: 'auto' }}>
                     <CheckCircleIcon />
                  </Avatar>
               </Badge>
               <Typography variant='body2'>Accepted</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
               <Badge
                  badgeContent={declinedCount}
                  color='error'
                  sx={{ '& .MuiBadge-badge': { top: 5, right: 5 } }}
               >
                  <Avatar sx={{ bgcolor: 'error.light', mb: 1, mx: 'auto' }}>
                     <CancelIcon />
                  </Avatar>
               </Badge>
               <Typography variant='body2'>Declined</Typography>
            </Box>
         </Box>

         <Divider sx={{ my: 2 }} />

         {pendingCount == 0 && acceptedCount == 0 && declinedCount == 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
               <GroupIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
               <Typography variant='body2' color='text.secondary' gutterBottom>
                  No invitations sent yet
               </Typography>
               <Button
                  variant='outlined'
                  size='small'
                  startIcon={<PersonAddIcon />}
                  onClick={handleCreateTrip}
                  sx={{ mt: 1 }}
               >
                  Create Trip & Invite Buddies
               </Button>
            </Box>
         )}

         {(pendingCount > 0 || acceptedCount > 0 || declinedCount > 0) && (
            <Box sx={{ textAlign: 'center' }}>
               <Button
                  component={Link}
                  to='/invitations'
                  size='small'
                  endIcon={<ArrowForwardIcon />}
               >
                  View All Invitations
               </Button>
            </Box>
         )}
      </>
   );
};

export default InvitationStatus;
